import { NgModule } from '@angular/core';
import { Routes, RouterModule, ExtraOptions } from '@angular/router';
import { ActivationComponent } from './core/account/activation/activation.component';
import { BaseGuardService } from './core/account/guards/base-guard.service';
import { CheckinGuardService } from './core/account/guards/checkin-guard.service';
import { FormRenderGuardService } from './core/account/guards/form-render-guard.service';
import { LoginComponent } from './core/account/login/login.component';
import { ResetPasswordComponent } from './core/account/reset-password/reset-password.component';
import { ResetRequestComponent } from './core/account/reset-request/reset-request.component';
import { SignupComponent } from './core/account/signup/signup.component';
import { FormTransactionComponent } from './core/form-transaction/form-transaction.component';
import { FormsComponent } from './core/forms/forms.component';
import { GroupsComponent } from './core/groups/groups.component';
import { ViewMembersComponent } from './core/groups/view-members/view-members.component';
import { HomeComponent } from './core/home/home.component';
import { MyDonationsComponent } from './core/my-donations/my-donations.component';
import { MyProfileComponent } from './core/my-profile/my-profile.component';
import { MyTasksComponent } from './core/my-tasks/my-tasks.component';
import { NotfoundComponent } from './core/navigation/notfound/notfound.component';
import { ServiceNotfoundComponent } from './core/navigation/service-notfound/service-notfound.component';
import { UnauthorizedComponent } from './core/navigation/unauthorized/unauthorized.component';
import { FastCheckinComponent } from './core/fast-checkin/fast-checkin.component';
import { MyStatementComponent } from './core/my-statement/my-statement.component';
import { QrSelfCheckinComponent } from './core/qr-self-checkin/qr-self-checkin.component';
import { QrSelfCheckinFixedComponent } from './core/qr-self-checkin-fixed/qr-self-checkin-fixed.component';
import { AddPointTransactionComponent } from './core/add-point-transaction/add-point-transaction.component';
import { SelectTenantComponent } from './core/select-tenant/select-tenant.component';
import { SwitchTenantComponent } from './core/switch-tenant/switch-tenant.component';
import { UnsubscribeComponent } from './admin/unsubscribe/unsubscribe.component';
import { PushNotificationsComponent } from './core/push-notifications/push-notifications.component';
import { LoginRedirectGuard } from './core/account/guards/login-redirect-guard.service';
import { PaymentSuccessComponent } from './core/form-transaction/payment-success/payment-success.component';
import { PaymentFailureComponent } from './core/form-transaction/payment-failure/payment-failure.component';
import { QrSelfCheckinGroupComponent } from './core/qr-self-checkin-group/qr-self-checkin-group.component';
import { QrSelfCheckinVenueComponent } from './core/qr-self-checkin-venue/qr-self-checkin-venue.component';

const routerOptions: ExtraOptions = {
  scrollPositionRestoration: 'enabled',
  // anchorScrolling: 'disabled',
  // onSameUrlNavigation: 'reload',
  // scrollOffset: [0, 20], 
};

const routes: Routes = [
  { path: '', component: HomeComponent, data: { animation: 0, reuse: false }, pathMatch: 'full' },
  { path: '', redirectTo: '/home', pathMatch: 'full' },
  { path: 'home', component: HomeComponent, data: { animation: 0, reuse: false } },
  { path: 'unsubscribe', component: UnsubscribeComponent },
  {
    path: ':tenantId', children: [
      { path: '', component: HomeComponent, data: { animation: 0, reuse: false }, pathMatch: 'full' },
      { path: 'unauthorized', component: UnauthorizedComponent },
      { path: 'signup', component: SignupComponent },
      { path: 'account-deletion', loadComponent: () => import('./core/my-profile/account-deletion-status.component').then(m => m.AccountDeletionStatusComponent) },
      { path: 'email-access', loadComponent: () => import('./core/account/activation/email-access.component').then(m => m.EmailAccessComponent) },
      { path: 'email-access/:token', loadComponent: () => import('./core/account/activation/email-access.component').then(m => m.EmailAccessComponent) },
      { path: 'validate/:email/:token', component: ActivationComponent },
      { path: 'resetrequest', component: ResetRequestComponent },
      { path: 'login', canActivate: [LoginRedirectGuard], component: LoginComponent },
      { path: 'fast-checkin', component: FastCheckinComponent },
      { path: 'fast-checkin/:function', component: FastCheckinComponent },
      { path: 'add-point/:memberId/:self', component: AddPointTransactionComponent },
      {
        path: 'qr-self-checkin/:eventId',
        component: QrSelfCheckinComponent,
      },
      {
        path: 'qr-self-checkin-fixed/:serviceId',
        component: QrSelfCheckinFixedComponent,
      },
      {
        path: 'qr-self-checkin-fixed-group/:groupId',
        component: QrSelfCheckinGroupComponent,
      },
      {
        path: 'qr-self-checkin-fixed-venue/:venueId',
        component: QrSelfCheckinVenueComponent,
      },
      {
        path: 'formrender/:formId',
        component: FormTransactionComponent,
      },
      {
        path: 'form-order/:orderId',
        loadComponent:()=>import('./shared/forms/form-order.component').then(m=>m.FormOrderComponent)
      },
      {
        path: 'payment-success/:formTransactionId',
        component: PaymentSuccessComponent
      },
      {
        path: 'payment-failed',
        component: PaymentFailureComponent
      },
      {
        path: 'formrenderguard/:formId',
        canActivate: [FormRenderGuardService],
        component: FormTransactionComponent,
      },
      {
        path: 'resetpassword/:email/:token',
        component: ResetPasswordComponent,
      },
      { path: 'content/:contentId', loadComponent: () => import('./shared/cms-studio/cms-content-page.component').then(m => m.CmsContentPageComponent) },
      { path: 'home', component: HomeComponent, data: { animation: 0, reuse: false } },
      { path: 'service-notfound', component: ServiceNotfoundComponent },
      { path: 'select-tenant', component: SelectTenantComponent },
      { path: 'switch-tenant', component: SwitchTenantComponent },
      { path: 'notifications', component: PushNotificationsComponent },
      {
        path: 'groups/:groupId',
        canActivate: [BaseGuardService],
        component: GroupsComponent,
      },
      {
        path: 'groups',
        canActivate: [BaseGuardService],
        component: GroupsComponent,
      },
      {
        path: 'groups/view-members/:groupId',
        canActivate: [BaseGuardService],
        component: ViewMembersComponent,
      },
      {
        path: 'myprofile',
        canActivate: [BaseGuardService],
        component: MyProfileComponent,
      },
      {
        path: 'notifications',
        component: MyProfileComponent,
      },
      {
        path: 'forms',
        canActivate: [BaseGuardService],
        component: FormsComponent,
      },
      {
        path: 'my-donations',
        canActivate: [BaseGuardService],
        component: MyDonationsComponent,
      },
      {
        path: 'my-statement/:year',
        canActivate: [BaseGuardService],
        component: MyStatementComponent,
      },
      {
        path: 'mytasks',
        canActivate: [BaseGuardService],
        component: MyTasksComponent,
      },
      {
        path: 'usher',
        canActivate: [CheckinGuardService],
        loadChildren: () =>
          import('./usher/usher.module').then((m) => m.UsherModule),
      },
      {
        path: 'admin',
        loadChildren: () =>
          import('./admin/admin.module').then((m) => m.AdminModule),
      },
      {
        path: 'member',
        canActivate: [BaseGuardService],
        loadChildren: () =>
          import('./member/member.module').then((m) => m.MemberModule),
      },
      { path: '404', component: NotfoundComponent },
      { path: '**', redirectTo: '/404' },
    ]
  }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, routerOptions),],
  exports: [RouterModule],
})
export class AppRoutingModule { }
